import { API_ROUTES } from "../../packages/shared/src/constants/index";
import { MatchSubmitPayload, MatchSubmitResponse, DailyBoxStatus, DailyBoxClaimResponse, PlayerProfile, PlayerSyncPayload, LeaderboardResponse, CatalogItemDTO, InvoiceResponse, BoosterKind, PurchaseResponse, ReferralInfo, ReferralClaimResponse, TonPurchaseResponse, TonDepositStatus, TributePurchaseResponse, TributeDepositStatus, TransactionHistoryResponse, DailyLeaderboardResponse } from "../../packages/shared/src/index";

const API_URL = (typeof process !== "undefined" && (process.env as any).API_URL) || "";

export type RemoteConfig = Record<string, unknown> & { _meta?: { telegramId: string | null; ts: number } };

class ApiClient {
  readonly baseUrl = API_URL;

  /** Cached tier values from last server response */
  spawnTier = 1;
  startPopTier = 1;
  offlineTier = 1;
  boosterGateBasePrice = 5;
  boosterGateMaxTier = 7;
  hardBalance = 0;

  /** Remote config fetched at startup */
  remoteConfig: RemoteConfig = {};

  /** Event batch queue */
  private _eventQueue: Array<{ type: string; payload?: Record<string, unknown>; ts: number }> = [];
  private _eventTimer: ReturnType<typeof setTimeout> | null = null;

  private updateTiers(profile: PlayerProfile) {
    if (profile) {
      this.spawnTier = profile.spawnTier ?? 1;
      this.startPopTier = profile.startPopTier ?? 1;
      this.offlineTier = profile.offlineTier ?? 1;
      this.boosterGateBasePrice = profile.boosterGateBasePrice ?? 5;
      this.boosterGateMaxTier = profile.boosterGateMaxTier ?? 7;
      this.hardBalance = profile.hardBalance ?? 0;
      this.emitHardBalanceUpdate();
    }
  }

  emitHardBalanceUpdate() {
    window.dispatchEvent(new CustomEvent("stars-balance-updated", { detail: this.hardBalance }));
  }

  private getInitData(): string {
    return (window as any).Telegram?.WebApp?.initData ?? "";
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {
      "Authorization": `tma ${this.getInitData()}`,
    };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }
    const res = await fetch(`${API_URL}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const text = await res.text();
    return text ? JSON.parse(text) : ({} as T);
  }

  async authPlayer(): Promise<PlayerProfile> {
    const profile = await this.request<PlayerProfile>("POST", API_ROUTES.PLAYER_AUTH);
    this.updateTiers(profile);
    return profile;
  }

  async getMe(): Promise<PlayerProfile> {
    const profile = await this.request<PlayerProfile>("GET", API_ROUTES.PLAYER_ME);
    this.updateTiers(profile);
    return profile;
  }

  async submitMatch(payload: MatchSubmitPayload): Promise<MatchSubmitResponse> {
    return this.request<MatchSubmitResponse>("POST", API_ROUTES.MATCH_SUBMIT, payload);
  }

  async getDailyBoxStatus(): Promise<DailyBoxStatus> {
    return this.request<DailyBoxStatus>("GET", API_ROUTES.DAILY_BOX_STATUS);
  }

  async claimDailyBox(): Promise<DailyBoxClaimResponse> {
    return this.request<DailyBoxClaimResponse>("POST", API_ROUTES.DAILY_BOX_CLAIM);
  }

  async getLeaderboard(type: "global" | "weekly" = "global"): Promise<LeaderboardResponse> {
    const path = type === "weekly" ? API_ROUTES.LEADERBOARD_WEEKLY : API_ROUTES.LEADERBOARD_GLOBAL;
    return this.request<LeaderboardResponse>("GET", path);
  }

  async getMyLeaderboardPosition(): Promise<{ globalRank: number | null; weeklyRank: number | null; xp: number }> {
    return this.request("GET", API_ROUTES.LEADERBOARD_ME);
  }

  async getCatalog(): Promise<CatalogItemDTO[]> {
    return this.request<CatalogItemDTO[]>("GET", API_ROUTES.SHOP_CATALOG);
  }

  async createInvoice(slug: string): Promise<InvoiceResponse> {
    return this.request<InvoiceResponse>("POST", API_ROUTES.SHOP_CREATE_INVOICE, { slug });
  }

  async syncProgress(payload: PlayerSyncPayload): Promise<PlayerProfile> {
    const profile = await this.request<PlayerProfile>("POST", API_ROUTES.PLAYER_SYNC, payload);
    this.updateTiers(profile);
    return profile;
  }

  async createBoosterUnlockInvoice(boosterType: BoosterKind): Promise<InvoiceResponse> {
    return this.request<InvoiceResponse>("POST", API_ROUTES.SHOP_UNLOCK_BOOSTER, { boosterType });
  }

  async purchase(slug: string): Promise<PurchaseResponse> {
    return this.request<PurchaseResponse>("POST", API_ROUTES.SHOP_PURCHASE, { slug });
  }

  async claimReferralGift(): Promise<void> {
    await this.request<{ ok: boolean }>("POST", API_ROUTES.PLAYER_CLAIM_REFERRAL_GIFT);
  }

  async getReferralInfo(): Promise<ReferralInfo> {
    return this.request<ReferralInfo>("GET", API_ROUTES.REFERRAL_INFO);
  }

  async claimReferralEarnings(): Promise<ReferralClaimResponse> {
    return this.request<ReferralClaimResponse>("POST", API_ROUTES.REFERRAL_CLAIM);
  }

  async prepareReferralShare(lang?: string): Promise<{ preparedMessageId: string }> {
    return this.request<{ preparedMessageId: string }>("POST", API_ROUTES.REFERRAL_PREPARE_SHARE, lang ? { lang } : undefined);
  }

  async purchaseTon(slug: string): Promise<TonPurchaseResponse> {
    return this.request<TonPurchaseResponse>("POST", API_ROUTES.SHOP_PURCHASE_TON, { slug });
  }

  async getTonDepositStatus(depositId: number): Promise<TonDepositStatus> {
    return this.request<TonDepositStatus>("GET", `${API_ROUTES.TON_DEPOSIT_STATUS}/${depositId}`);
  }

  async getTransactions(limit = 50, offset = 0, filter?: string): Promise<TransactionHistoryResponse> {
    let path = `${API_ROUTES.PLAYER_TRANSACTIONS}?limit=${limit}&offset=${offset}`;
    if (filter && filter !== "all") path += `&filter=${filter}`;
    return this.request<TransactionHistoryResponse>("GET", path);
  }

  async getTonRate(): Promise<{ rate: number }> {
    return this.request<{ rate: number }>("GET", API_ROUTES.TON_RATE);
  }

  async purchaseTribute(slug: string): Promise<TributePurchaseResponse> {
    return this.request<TributePurchaseResponse>("POST", API_ROUTES.SHOP_PURCHASE_TRIBUTE, { slug });
  }

  async getTributeDepositStatus(depositId: number): Promise<TributeDepositStatus> {
    return this.request<TributeDepositStatus>("GET", `${API_ROUTES.TRIBUTE_DEPOSIT_STATUS}/${depositId}`);
  }

  async getDailyLeaderboard(): Promise<DailyLeaderboardResponse> {
    return this.request<DailyLeaderboardResponse>("GET", API_ROUTES.LEADERBOARD_DAILY);
  }

  async getMyDailyPosition(): Promise<{ rank: number | null; score: number; pendingReward: any }> {
    return this.request("GET", API_ROUTES.LEADERBOARD_DAILY_ME);
  }

  async claimDailyLeaderboardReward(): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>("POST", API_ROUTES.LEADERBOARD_DAILY_CLAIM);
  }

  async getConfig(): Promise<RemoteConfig> {
    try {
      const config = await this.request<RemoteConfig>("GET", API_ROUTES.CONFIG);
      this.remoteConfig = config;
      return config;
    } catch {
      return this.remoteConfig;
    }
  }

  /** Get a config value with typed fallback */
  configValue<T>(key: string, fallback: T): T {
    const val = this.remoteConfig[key];
    return val !== undefined ? (val as T) : fallback;
  }

  /** Fire-and-forget event tracking with 10s batching */
  trackEvent(type: string, payload?: Record<string, unknown>) {
    this._eventQueue.push({ type, payload, ts: Date.now() });
    if (!this._eventTimer) {
      this._eventTimer = setTimeout(() => this._flushEvents(), 10_000);
    }
  }

  private async _flushEvents() {
    this._eventTimer = null;
    if (this._eventQueue.length === 0) return;
    const batch = this._eventQueue.splice(0);
    try {
      await this.request("POST", API_ROUTES.EVENTS, { events: batch });
    } catch {
      // Non-critical — silently drop
    }
  }

  /** Flush events immediately (for beforeunload) */
  flushEventsSync() {
    if (this._eventTimer) {
      clearTimeout(this._eventTimer);
      this._eventTimer = null;
    }
    if (this._eventQueue.length === 0) return;
    const batch = this._eventQueue.splice(0);
    const initData = (window as any).Telegram?.WebApp?.initData || "";
    try {
      fetch(`${API_URL}${API_ROUTES.EVENTS}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `tma ${initData}` },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  }

  /** Fire-and-forget funnel event (separate from A/B events, no batching) */
  trackFunnelEvent(event: string, metadata?: Record<string, unknown>) {
    this.request("POST", API_ROUTES.FUNNEL_EVENT, { event, metadata }).catch(() => {});
  }

  async reportAdContinue(): Promise<{ok: boolean}> {
    return this.request<{ok: boolean}>("POST", API_ROUTES.ADS_CONTINUE, {});
  }

  async reportRewardedComplete(): Promise<{ views: number; milestones: Array<{ views: number; reward: number; type: "soft" | "hard"; claimed: boolean }>; claimedRewards: Array<{ milestone: number; reward: number; type: string }> }> {
    return this.request("POST", API_ROUTES.ADS_REWARDED_COMPLETE, {});
  }

  async getDailyAdProgress(): Promise<{ views: number; milestones: Array<{ views: number; reward: number; type: "soft" | "hard"; claimed: boolean }> }> {
    return this.request("GET", API_ROUTES.ADS_DAILY_PROGRESS);
  }

  async premiumClaimAd(): Promise<{ views: number; milestones: Array<{ views: number; reward: number; type: "soft" | "hard"; claimed: boolean }>; claimedRewards: Array<{ milestone: number; reward: number; type: string }> }> {
    return this.request("POST", API_ROUTES.ADS_PREMIUM_CLAIM, {});
  }

  /** Imperative purchase flow: balance-first, then TG invoice if needed */
  async purchaseFlow(slug: string): Promise<{paid: boolean; result?: PurchaseResponse}> {
    const result = await this.purchase(slug);
    if (result.method === "balance") {
      this.hardBalance = result.newHardBalance ?? 0;
      this.emitHardBalanceUpdate();
      return {paid: true, result};
    }
    const tg = (window as any).Telegram?.WebApp;
    if (!tg?.openInvoice || !result.url) return {paid: false};
    return new Promise((resolve) => {
      tg.openInvoice(result.url!, (status: string) => {
        if (status === "paid") {
          this.hardBalance = Math.max(0, this.hardBalance - (result.balanceUsed ?? 0));
          this.emitHardBalanceUpdate();
          resolve({paid: true, result});
        } else {
          resolve({paid: false});
        }
      });
    });
  }
}

export const apiClient = new ApiClient();
